import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { CheckKind, Monitor, Recipient } from '../types/monitor';
import type { AlertRecord, Invoice } from '../types/alert';
import type { BillingPeriod, Plan, PlanId } from '../types/plan';
import { getPlan } from '../data/plans';
import { monitors as seedMonitors, recipients as seedRecipients } from '../data/monitors';
import { alertHistory, invoices as seedInvoices } from '../data/alerts';
import { servicesUsed as countServices } from '../utils/health';
import { daysFromNow, minutesFromNow } from '../utils/datetime';
export interface NotificationDefaults {
  warningWindowDays: number;
  reminderCadence: number[];
  pingIntervalMinutes: number;
  failureThreshold: number;
  quietHoursEnabled: boolean;
  quietFrom: string;
  quietTo: string;
  cadence: 'immediate' | 'daily-digest';
  slackWebhook: string;
  webhookUrl: string;
}
export interface Profile {
  name: string;
  email: string;
  company: string;
  timezone: string;
}
export interface NewMonitorDraft {
  domain: string;
  label: string;
  sslEnabled: boolean;
  uptimeEnabled: boolean;
  warningWindowDays: number;
  reminderCadence: number[];
  pingIntervalMinutes: number;
  method: Monitor['method'];
  expectedStatus: number;
  timeoutSeconds: number;
  failureThreshold: number;
  recipientIds: string[];
}
interface AccountValue {
  profile: Profile;
  planId: PlanId;
  plan: Plan;
  billingPeriod: BillingPeriod;
  monitors: Monitor[];
  recipients: Recipient[];
  alerts: AlertRecord[];
  invoices: Invoice[];
  defaults: NotificationDefaults;
  servicesUsed: number;
  serviceLimit: number;
  servicesRemaining: number;
  justSubscribed: boolean;
  teamSeats: {
    id: string;
    name: string;
    email: string;
    role: string;
  }[];
  updateProfile: (patch: Partial<Profile>) => void;
  setPlanId: (id: PlanId) => void;
  setBillingPeriod: (period: BillingPeriod) => void;
  toggleCheck: (monitorId: string, kind: CheckKind) => void;
  updateMonitor: (monitorId: string, patch: Partial<Monitor>) => void;
  addMonitor: (draft: NewMonitorDraft) => string;
  removeMonitor: (monitorId: string) => void;
  pauseMonitors: (monitorIds: string[]) => void;
  addRecipient: (email: string) => void;
  removeRecipient: (recipientId: string) => void;
  verifyRecipient: (recipientId: string) => void;
  updateDefaults: (patch: Partial<NotificationDefaults>) => void;
  markSubscribed: () => void;
  dismissSubscribedBanner: () => void;
  touchMonitor: (monitorId: string) => void;
}
const AccountContext = createContext<AccountValue | null>(null);
export function AccountProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [profile, setProfile] = useState<Profile>({
    name: 'Priya Raman',
    email: 'priya@acme.com',
    company: 'Acme Internet',
    timezone: 'Asia/Kolkata'
  });
  const [planId, setPlanId] = useState<PlanId>('pro');
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [monitors, setMonitors] = useState<Monitor[]>(seedMonitors);
  const [recipients, setRecipients] = useState<Recipient[]>(seedRecipients);
  const [alerts] = useState<AlertRecord[]>(alertHistory);
  const [invoices] = useState<Invoice[]>(seedInvoices);
  const [justSubscribed, setJustSubscribed] = useState(false);
  const [defaults, setDefaults] = useState<NotificationDefaults>({
    warningWindowDays: 21,
    reminderCadence: [30, 14, 7, 1],
    pingIntervalMinutes: 5,
    failureThreshold: 2,
    quietHoursEnabled: false,
    quietFrom: '22:00',
    quietTo: '07:00',
    cadence: 'immediate',
    slackWebhook: '',
    webhookUrl: ''
  });
  const plan = getPlan(planId);
  const servicesUsed = countServices(monitors);
  const serviceLimit = plan.services;
  const teamSeats = useMemo(() => [{
    id: 't1',
    name: 'Priya Raman',
    email: 'priya@acme.com',
    role: 'Owner'
  }, {
    id: 't2',
    name: 'Dan Whitfield',
    email: 'dan@acme.com',
    role: 'Admin'
  }, {
    id: 't3',
    name: 'Operations rota',
    email: 'ops@acme.com',
    role: 'Viewer'
  }], []);
  const updateProfile = useCallback((patch: Partial<Profile>) => {
    setProfile(current => ({
      ...current,
      ...patch
    }));
  }, []);
  const updateMonitor = useCallback((monitorId: string, patch: Partial<Monitor>) => {
    setMonitors(current => current.map(monitor => monitor.id === monitorId ? {
      ...monitor,
      ...patch
    } : monitor));
  }, []);
  const toggleCheck = useCallback((monitorId: string, kind: CheckKind) => {
    setMonitors(current => {
      const used = countServices(current);
      return current.map(monitor => {
        if (monitor.id !== monitorId) {
          return monitor;
        }
        const key = kind === 'ssl' ? 'sslEnabled' : 'uptimeEnabled';
        const nextValue = !monitor[key];
        if (nextValue && used >= serviceLimit) {
          return monitor;
        }
        return {
          ...monitor,
          [key]: nextValue
        };
      });
    });
  }, [serviceLimit]);
  const addMonitor = useCallback((draft: NewMonitorDraft) => {
    const id = `m${Date.now()}`;
    const monitor: Monitor = {
      id,
      domain: draft.domain,
      label: draft.label || 'Untitled site',
      sslEnabled: draft.sslEnabled,
      uptimeEnabled: draft.uptimeEnabled,
      certificate: {
        subject: `CN=${draft.domain}`,
        issuer: "Let's Encrypt R11",
        validFrom: daysFromNow(-6),
        validTo: daysFromNow(84),
        serial: '05:C1:44:9B:2E:70:AF:31:6D:12',
        algorithm: 'ECDSA P-256, SHA-256',
        chainValid: true,
        altNames: [draft.domain]
      },
      certificateError: null,
      warningWindowDays: draft.warningWindowDays,
      reminderCadence: draft.reminderCadence,
      pingIntervalMinutes: draft.pingIntervalMinutes,
      method: draft.method,
      expectedStatus: draft.expectedStatus,
      timeoutSeconds: draft.timeoutSeconds,
      failureThreshold: draft.failureThreshold,
      isUp: true,
      responseTimes: Array.from({
        length: 48
      }, (_, index) => Math.round(180 + Math.sin(index / 3) * 28)),
      availability24h: 100,
      availability7d: 100,
      availability30d: 100,
      incidents: [],
      lastCheckedAt: minutesFromNow(-1),
      nextCheckAt: minutesFromNow(draft.pingIntervalMinutes),
      recipientIds: draft.recipientIds
    };
    setMonitors(current => [monitor, ...current]);
    return id;
  }, []);
  const removeMonitor = useCallback((monitorId: string) => {
    setMonitors(current => current.filter(m => m.id !== monitorId));
  }, []);
  const pauseMonitors = useCallback((monitorIds: string[]) => {
    setMonitors(current => current.map(monitor => monitorIds.includes(monitor.id) ? {
      ...monitor,
      sslEnabled: false,
      uptimeEnabled: false
    } : monitor));
  }, []);
  const addRecipient = useCallback((email: string) => {
    setRecipients(current => [...current, {
      id: `r${Date.now()}`,
      email,
      verified: false
    }]);
  }, []);
  const removeRecipient = useCallback((recipientId: string) => {
    setRecipients(current => current.filter(r => r.id !== recipientId));
    setMonitors(current => current.map(monitor => ({
      ...monitor,
      recipientIds: monitor.recipientIds.filter(id => id !== recipientId)
    })));
  }, []);
  const verifyRecipient = useCallback((recipientId: string) => {
    setRecipients(current => current.map(r => r.id === recipientId ? {
      ...r,
      verified: true
    } : r));
  }, []);
  const updateDefaults = useCallback((patch: Partial<NotificationDefaults>) => {
    setDefaults(current => ({
      ...current,
      ...patch
    }));
  }, []);
  const touchMonitor = useCallback((monitorId: string) => {
    updateMonitor(monitorId, {
      lastCheckedAt: new Date().toISOString()
    });
  }, [updateMonitor]);
  const value: AccountValue = {
    profile,
    planId,
    plan,
    billingPeriod,
    monitors,
    recipients,
    alerts,
    invoices,
    defaults,
    servicesUsed,
    serviceLimit,
    servicesRemaining: Math.max(0, serviceLimit - servicesUsed),
    justSubscribed,
    teamSeats,
    updateProfile,
    setPlanId,
    setBillingPeriod,
    toggleCheck,
    updateMonitor,
    addMonitor,
    removeMonitor,
    pauseMonitors,
    addRecipient,
    removeRecipient,
    verifyRecipient,
    updateDefaults,
    markSubscribed: () => setJustSubscribed(true),
    dismissSubscribedBanner: () => setJustSubscribed(false),
    touchMonitor
  };
  return <AccountContext.Provider value={value} data-id="element-476" data-mp="2j63ri7ju97cz">{children}</AccountContext.Provider>;
}
export function useAccount(): AccountValue {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used inside AccountProvider');
  }
  return context;
}